import {
  clone,
  duplicateControlSignature,
  holdPaidMarketingUntilApproval,
  marketingBudgetBasisSignature
} from "./release2BState.js";
import { captureBaseAgentFields, restoreBaseAgentFields } from "./release2BQcCorrections.js";

function changed(left, right) {
  return JSON.stringify(left ?? null) !== JSON.stringify(right ?? null);
}

function committedBaseTransition(previousSession, nextSession) {
  const previous = previousSession?.record;
  const next = nextSession?.record;
  if (!previous || !next) return false;
  if (previous.status !== "Needs Changes") return true;
  if (next.status !== "Needs Changes") return true;
  return previous.dirty === true && next.dirty === false;
}

function baseComparisonRecord(previousSession) {
  if (previousSession?.record?.status === "Needs Changes" && previousSession.baseSavedDraft) {
    return restoreBaseAgentFields(previousSession.record, previousSession.baseSavedDraft);
  }
  return previousSession?.record || null;
}

function invalidateBudgetApproval(record, reason) {
  let next = clone(record);
  const hadDecision = next.decisions?.budget === "Approved" || (next.decisions?.budget === "Pending" && next.marketingSubmitted === true);
  if (!hadDecision) return next;
  const priorVersionId = next.marketingSubmittedVersionId || next.marketingDecisionVersionId;
  if (priorVersionId) {
    next.marketingVersions = (next.marketingVersions || []).map(version => version.id === priorVersionId ? { ...version, stale: true, staleReason: reason } : version);
  }
  next.decisions = { ...next.decisions, budget: "Pending" };
  next.marketingSubmitted = false;
  next.marketingSubmittedBudgetBasis = "";
  next.marketingRequiredExceptionApproved = false;
  next.marketingRequiredExceptionBasis = "";
  next.marketingRequiredExceptionVersionId = "";
  next.marketingRevisionRequired = true;
  next.allowMarketingEdit = true;
  return holdPaidMarketingUntilApproval(next);
}

export function normalizeRelease2BTransition(previousSession, proposedSession) {
  if (!proposedSession) return proposedSession;
  const next = clone(proposedSession);
  if (!previousSession?.record) return next;

  if (next.baseSavedDraft?.kind) next.baseSavedDraft = captureBaseAgentFields(next.baseSavedDraft);
  if (next.record && !next.record.kind && previousSession.record?.kind) {
    next.record = restoreBaseAgentFields(previousSession.record, next.record);
    next.record.dirty = false;
  }
  if (!next.record) return next;

  const before = baseComparisonRecord(previousSession);
  const after = next.record;
  const committed = committedBaseTransition(previousSession, next);

  if (committed) {
    if (duplicateControlSignature(before) !== duplicateControlSignature(after)) {
      after.duplicateAcknowledged = false;
      after.duplicateResolutionNote = "";
      after.duplicateAckBasis = "";
    }
    if (
      changed(before.client?.id, after.client?.id)
      || changed(before.assignedAgentId, after.assignedAgentId)
      || changed(before.assignedAgentMatchesIntakeUser, after.assignedAgentMatchesIntakeUser)
      || changed(before.intakeUserEmail, after.intakeUserEmail)
    ) {
      after.agentMismatchReviewed = false;
      after.agentMismatchReviewBasis = "";
    }
    if (after.kind === "listing" && marketingBudgetBasisSignature(before) !== marketingBudgetBasisSignature(after)) {
      next.record = invalidateBudgetApproval(after, "Proposed list price or commission changed the Marketing budget basis.");
    }
    if (next.record.status === "Needs Changes" && next.record.dirty === false) {
      next.baseSavedDraft = captureBaseAgentFields(next.record);
    }
  }

  if (next.record.agentMismatchReviewed && next.record.agentMismatchReviewBasis) {
    const currentBasis = agentMismatchReviewBasis(next.record);
    if (currentBasis !== next.record.agentMismatchReviewBasis) {
      next.record.agentMismatchReviewed = false;
      next.record.agentMismatchReviewBasis = "";
    }
  }
  return next;
}

export function marketingLeadershipActionable(record) {
  return Boolean(record?.kind === "listing" && record.marketingSubmitted === true && record.decisions?.budget === "Pending" && !record.marketingRevisionRequired && record.marketingSubmittedVersionId && record.marketingSubmittedBudgetBasis === marketingBudgetBasisSignature(record));
}
export function openHouseLeadershipActionable(record) {
  return Boolean(record?.kind === "listing" && record.openHouseSubmitted === true && record.decisions?.openHouse === "Pending" && record.openHouseSubmittedVersionId);
}
export function openHouseApprovalReady(record) {
  return Boolean(!record?.openHouseRequested || (record.openHouseDate && record.openHouseStart && record.openHouseEnd && String(record.openHouseHost || "").trim()));
}
export function agentMismatchReviewBasis(record) {
  return [
    record?.client?.id || "",
    record?.assignedAgentId || "",
    record?.intakeUserEmail || "",
    record?.assignedAgentMatchesIntakeUser === false ? "mismatch" : record?.assignedAgentMatchesIntakeUser === true ? "match" : "unresolved"
  ].join("|");
}
